/**
 * Legal notice (snippets/legal-notice.liquid, snippets/legal-notice-lightbox.liquid)
 * - Text display: a link with [data-legal-notice-dialog="dialog-id"] opens its PDF in that dialog on larger screens.
 *   On phones, or when opened with a modifier key, it stays a normal link that opens in a new tab.
 * - Compact display: accordion toggle, lightbox with the official graphic, tooltip in the cart bar
 * - Full display: the cart bar scrolls to the graphic in the line items area
 * Uses event delegation, so it keeps working after the theme re-renders the product info or the cart drawer.
 */

if (!window.legalNoticeReady) {
  window.legalNoticeReady = true;

  const isPhone = () => !window.matchMedia('(min-width: 700px)').matches;
  const prefersReducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  let lastTrigger = null;

  const loadDocument = (dialog) => {
    const frame = dialog.querySelector('iframe[data-src]');

    if (frame && !frame.src) {
      frame.src = frame.dataset.src;
    }
  };

  const openDialog = (dialog, trigger) => {
    lastTrigger = trigger;
    loadDocument(dialog);
    document.documentElement.style.overflow = 'hidden';
    dialog.showModal();
  };

  const closeTooltips = (except) => {
    document.querySelectorAll('[data-legal-notice-tooltip].is-open').forEach((tooltip) => {
      if (tooltip !== except) {
        tooltip.classList.remove('is-open');
        tooltip.querySelector('[data-legal-notice-tooltip-toggle]')?.setAttribute('aria-expanded', 'false');
      }
    });
  };

  document.addEventListener('click', (event) => {
    const target = event.target;

    // Text display: PDF popup
    const dialogTrigger = target.closest('[data-legal-notice-dialog]');

    if (dialogTrigger) {
      const dialog = document.getElementById(dialogTrigger.getAttribute('data-legal-notice-dialog'));

      if (!dialog || isPhone() || event.metaKey || event.ctrlKey || event.shiftKey) {
        return;
      }

      event.preventDefault();
      openDialog(dialog, dialogTrigger);
      return;
    }

    // Compact display: lightbox. Phones cannot show a PDF inside it, so without an image the PDF opens in a new tab.
    const lightboxTrigger = target.closest('[data-legal-notice-lightbox]');

    if (lightboxTrigger) {
      const dialog = document.getElementById(lightboxTrigger.getAttribute('data-legal-notice-lightbox'));
      const documentUrl = lightboxTrigger.getAttribute('data-legal-notice-document');

      if (!dialog) {
        return;
      }

      if (isPhone() && !dialog.querySelector('img') && documentUrl) {
        window.open(documentUrl, '_blank', 'noopener');
        return;
      }

      openDialog(dialog, lightboxTrigger);
      return;
    }

    // Compact display: accordion. The row and the chevron share the same panel.
    const toggle = target.closest('[data-legal-notice-toggle]');

    if (toggle) {
      const accordion = toggle.closest('[data-legal-notice-accordion]');
      const panel = document.getElementById(toggle.getAttribute('aria-controls'));
      const open = !accordion.classList.contains('is-open');

      accordion.classList.toggle('is-open', open);
      accordion.querySelectorAll('[data-legal-notice-toggle]').forEach((button) => button.setAttribute('aria-expanded', String(open)));
      panel.inert = !open;
      return;
    }

    // Full display: jump from the cart bar to the graphic in the line items area
    const scrollTrigger = target.closest('[data-legal-notice-scroll]');

    if (scrollTrigger) {
      document.getElementById(scrollTrigger.getAttribute('data-legal-notice-scroll'))?.scrollIntoView({
        behavior: prefersReducedMotion() ? 'auto' : 'smooth',
        block: 'start'
      });
      return;
    }

    // Cart bar: tooltip opens on tap and closes via its button, Escape or a tap outside
    const tooltipToggle = target.closest('[data-legal-notice-tooltip-toggle]');

    if (tooltipToggle) {
      const tooltip = tooltipToggle.closest('[data-legal-notice-tooltip]');
      const open = !tooltip.classList.contains('is-open');

      closeTooltips(tooltip);
      tooltip.classList.toggle('is-open', open);
      tooltipToggle.setAttribute('aria-expanded', String(open));
      return;
    }

    if (target.closest('[data-legal-notice-tooltip-close]')) {
      closeTooltips();
      return;
    }

    if (!target.closest('[data-legal-notice-tooltip]')) {
      closeTooltips();
    }

    // Close a dialog via its buttons or a click on the backdrop (the dialog element itself)
    const openedDialog = target.closest('.legal-notice-dialog, .legal-notice-lightbox');

    if (openedDialog && (target === openedDialog || target.closest('.legal-notice-dialog__close, [data-legal-notice-close]'))) {
      openedDialog.close();
    }
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') {
      closeTooltips();
    }
  });

  // The close event does not bubble, so listen in the capture phase
  document.addEventListener('close', (event) => {
    if (event.target.matches?.('.legal-notice-dialog, .legal-notice-lightbox')) {
      document.documentElement.style.removeProperty('overflow');

      if (lastTrigger?.isConnected) {
        lastTrigger.focus();
      }

      lastTrigger = null;
    }
  }, true);
}
